import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { AlertTriangle, FileText } from "lucide-react";

// -----------------------------------------------------------------------------
// LEGAL POLICY DRAFTS
// -----------------------------------------------------------------------------
// !! DRAFT — REQUIRES COUNSEL REVIEW BEFORE PUBLIC BETA !!
// These are placeholder copies for internal review only. They are NOT legal
// advice, NOT enforceable, and MUST be reviewed and replaced by qualified
// counsel in each operating jurisdiction before any real user is asked to
// accept them. Do not remove the DRAFT banner until legal sign-off is on file.
// -----------------------------------------------------------------------------

const POLICIES = {
  "helper-agreement": {
    title: "CLING Helper Agreement",
    version: "0.1 (draft)",
    updated: "2026-02",
    summary:
      "The terms under which someone provides help to another person through CLING — as a volunteer, a Kudos recipient, or a paid Immediate Help provider.",
    sections: [
      {
        h: "1. Who this covers",
        p: "You are a 'Helper' any time you enter a Help Room to assist another CLING user, whether the assistance is offered for free, in exchange for a Kudos tip, or as paid Immediate Help.",
      },
      {
        h: "2. Independent contractor, not employee",
        p: "You are an independent contractor. CLING does not employ Helpers, does not withhold tax, does not provide benefits, and does not direct how you deliver help. You are responsible for your own tax reporting in every jurisdiction where you receive Kudos or Immediate Help payments.",
      },
      {
        h: "3. Scope of help",
        p: "Help you provide is informal peer assistance. It is NOT a substitute for licensed professional advice — including medical, legal, financial, engineering, or safety-of-life guidance. If a question is beyond your competence or crosses into professional practice regulated in the asker's jurisdiction, refuse the request and refer the asker to a licensed professional.",
      },
      {
        h: "4. Safety obligations",
        p: "You will not instruct anyone to perform an action that you know or reasonably should know is unsafe. For Requests flagged 'caution', 'high risk', or 'emergency', you will explicitly acknowledge the risk classification before providing steps, and you will not knowingly override CLING's automated safety guardrails.",
      },
      {
        h: "5. Verification and conduct",
        p: "You will not misrepresent credentials, license status, or experience. If you claim a verification level (Community, Experience, Credential, Expert), you agree that CLING may request supporting evidence and may revoke the level at any time.",
      },
      {
        h: "6. Kudos and Immediate Help payments",
        p: "Kudos and Immediate Help fees are processed through CLING's payment infrastructure. CLING deducts a platform fee that is pinned on each transaction at the time it is completed — later changes to platform-wide fees will not retroactively alter payments already recorded. You will receive a payout statement per period once the payment gateway is live.",
      },
      {
        h: "7. Prohibited conduct",
        p: "You will not solicit off-platform payment, share contact information to bypass CLING, harass or dox users, impersonate another person, copy paid Immediate Help material into free public Solutions, or use any Help Room to sell unrelated products.",
      },
      {
        h: "8. Termination",
        p: "CLING may suspend or terminate your Helper access at any time, with or without notice, for violation of this agreement or of the CLING Community Guidelines. You may cease helping at any time. Pending kudos or payouts already earned before termination remain due to you.",
      },
      {
        h: "9. Governing law and disputes",
        p: "[PLACEHOLDER] Governing law, venue, arbitration/class-waiver terms, and consumer-protection carve-outs must be finalized by counsel per the jurisdictions CLING operates in.",
      },
    ],
  },

  "immediate-help-terms": {
    title: "Immediate Help — Paid Session Terms",
    version: "0.1 (draft)",
    updated: "2026-02",
    summary:
      "Terms governing paid, time-boxed Immediate Help sessions between an asker and a Helper.",
    sections: [
      {
        h: "1. What Immediate Help is",
        p: "Immediate Help is a paid, time-boxed help session (15, 30, or 60 minutes) between an asker and a Helper. It is a peer-to-peer help session, not licensed professional advice.",
      },
      {
        h: "2. Pricing and platform fee",
        p: "The asker pays the Helper's declared rate plus a platform fee set by CLING. The platform fee is pinned at the moment the session begins and is disclosed to the asker prior to authorization.",
      },
      {
        h: "3. Authorization vs. capture",
        p: "When you request Immediate Help, your payment method is authorized (a temporary hold) for the session total. Funds are captured only after the session ends successfully. If no Helper accepts within the session-open window, the authorization is released.",
      },
      {
        h: "4. Cancellation & refunds",
        p: "See our separate Cancellation & Refund Policy. In short: full refund before a Helper accepts, prorated refund if the Helper is unresponsive, and refund at CLING's discretion for material quality issues raised within 24 hours.",
      },
      {
        h: "5. Recording and evidence",
        p: "Help Rooms preserve the full text/photo transcript. Neither party may audio- or video-record without explicit consent from the other. The transcript may be reviewed by CLING's Trust & Safety team for dispute resolution only.",
      },
      {
        h: "6. Safety escalation",
        p: "If a request is classified 'emergency', Immediate Help is disabled and CLING routes the asker to appropriate professional services. Helpers who accept 'high risk' requests must acknowledge the risk classification before starting the session.",
      },
      {
        h: "7. Off-platform circumvention",
        p: "Both parties agree to complete the paid session within CLING. Moving the conversation off-platform to avoid the platform fee is a material breach and grounds for immediate account suspension for either party.",
      },
      {
        h: "8. Limitation of liability",
        p: "[PLACEHOLDER] Limits on damages, exclusions, and consumer-law carve-outs must be finalized by counsel per applicable jurisdictions.",
      },
    ],
  },

  "refund-policy": {
    title: "Cancellation & Refund Policy",
    version: "0.1 (draft)",
    updated: "2026-02",
    summary:
      "How refunds work for Immediate Help sessions, Kudos, and Helper Pro subscriptions.",
    sections: [
      {
        h: "1. Immediate Help — before a Helper accepts",
        p: "You may cancel at any time before a Helper accepts your Immediate Help session. The authorization on your payment method is released in full — no charge is captured.",
      },
      {
        h: "2. Immediate Help — after a Helper accepts",
        p: "If a Helper accepts but does not respond within 5 minutes, you may cancel for a full refund. If the Helper delivers materially incomplete or off-topic guidance, contact CLING within 24 hours to request a partial or full refund at our discretion.",
      },
      {
        h: "3. Immediate Help — session already completed",
        p: "Once a session ends successfully, it is considered delivered. Refund requests after that point are handled on a good-faith case-by-case basis; documented safety issues or clear service failure are always considered.",
      },
      {
        h: "4. Kudos",
        p: "Kudos are voluntary appreciation payments and are non-refundable once sent, except in cases of fraud, mistaken recipient identified within 15 minutes of sending, or Trust & Safety intervention.",
      },
      {
        h: "5. Helper Pro subscription",
        p: "Helper Pro is billed monthly or annually. You may cancel at any time from your Helper Pro page; your subscription remains active through the end of the billing period you have already paid for. No prorated refunds for partial periods, except where required by local consumer-protection law.",
      },
      {
        h: "6. Payment disputes and chargebacks",
        p: "Please contact CLING support before initiating a chargeback — most disputes are resolved faster in-app. Chargebacks that bypass this process may result in temporary account suspension while we investigate.",
      },
      {
        h: "7. Statutory rights",
        p: "[PLACEHOLDER] Nothing in this policy limits statutory refund or consumer-protection rights that apply in your jurisdiction. Counsel to add jurisdiction-specific carve-outs (EU 14-day right of withdrawal for digital services, UK CRA, US state-specific requirements, etc.).",
      },
    ],
  },

  "discovery-consent": {
    title: "Discovery Engine — Public Solution Consent",
    version: "0.1 (draft)",
    updated: "2026-02",
    summary:
      "How CLING treats consent when a Help Room is converted into a public Solution page discoverable outside the app.",
    sections: [
      {
        h: "1. What Discovery does",
        p: "When a Help Request is resolved, the asker may optionally consent to publish a sanitized, non-identifying Solution Page that is publicly discoverable and indexed by search engines. Publication is off by default. Two explicit and separate consents are required: (1) 'save as reusable knowledge' and (2) 'publish publicly'.",
      },
      {
        h: "2. Sanitization",
        p: "Before publication, CLING automatically redacts email addresses, phone numbers, and external URLs from the problem statement, steps, and solution. If you spot personal information that survived redaction, use the Report button on the Solution page and we will remove it.",
      },
      {
        h: "3. Attribution",
        p: "The asker is never named in the public Solution. Helpers who contributed may be attributed by display name and verification level, unless they have set their profile to 'no public attribution'. Helpers can revoke attribution at any time by changing that setting or requesting unpublish.",
      },
      {
        h: "4. Right to unpublish",
        p: "Any contributor (asker or Helper) may request the Solution be unpublished at any time and for any reason. Unpublishing removes it from public listings and search-engine sitemaps within a reasonable time; already-cached copies on third-party search engines will be re-crawled on their own schedule.",
      },
      {
        h: "5. Photos and annotations",
        p: "Photos submitted in a Help Room are private by default and are NOT copied into public Solutions unless the asker specifically opts in during the publish flow. Helper annotations on those photos follow the same rule.",
      },
      {
        h: "6. Personal data (GDPR/CCPA)",
        p: "[PLACEHOLDER] CLING is the data controller for public Solution content. Counsel to finalize: lawful basis for processing (consent + legitimate interest), data subject rights (access, rectification, erasure, portability), retention periods, DPO contact, and international transfer safeguards. See our Privacy Policy for the umbrella terms.",
      },
      {
        h: "7. Reporting content",
        p: "If you find a public Solution that contains personal information, unsafe advice, or copyrighted material, use the Report link on that page. We aim to review reports within 48 hours.",
      },
    ],
  },
};

// -----------------------------------------------------------------------------
// UI
// -----------------------------------------------------------------------------
function DraftBanner() {
  return (
    <div
      data-testid="legal-draft-banner"
      className="mb-6 flex items-start gap-3 rounded-2xl border border-[color:hsl(var(--amber))] bg-[color:hsl(var(--amber)/0.08)] p-4 text-sm"
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 flex-none text-[color:hsl(var(--amber))]" />
      <div>
        <div className="font-mono-label text-xs font-semibold uppercase tracking-wider text-[color:hsl(var(--amber))]">
          DRAFT — internal review copy only
        </div>
        <p className="mt-1 text-muted-foreground">
          These documents are placeholders. They have NOT been reviewed by counsel and MUST be
          replaced before the first public beta invite goes out. Do not accept, present, or
          rely on these terms as legally binding.
        </p>
      </div>
    </div>
  );
}

function PolicyIndex() {
  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <div className="text-xs font-mono-label text-[color:hsl(var(--amber))]">Legal</div>
        <h1 className="mt-2 font-display font-extrabold text-3xl sm:text-4xl">Policies &amp; Agreements</h1>
        <p className="mt-3 text-muted-foreground">
          Placeholder drafts covering how CLING works for Helpers, paid sessions, refunds, and
          public Solutions. Every document below is marked DRAFT until legal counsel signs off.
        </p>
        <DraftBanner />
        <ul className="mt-4 space-y-3" data-testid="legal-policy-list">
          {Object.entries(POLICIES).map(([slug, p]) => (
            <li key={slug}>
              <Link
                to={`/legal/${slug}`}
                data-testid={`legal-link-${slug}`}
                className="group flex items-start gap-3 rounded-2xl border bg-card p-5 transition hover:border-[color:hsl(var(--teal))]"
              >
                <FileText className="mt-0.5 h-5 w-5 flex-none text-[color:hsl(var(--teal))]" />
                <div>
                  <div className="flex items-baseline gap-2">
                    <span className="font-display font-bold text-lg group-hover:text-[color:hsl(var(--teal))]">
                      {p.title}
                    </span>
                    <span className="font-mono-label text-[10px] uppercase tracking-wider text-[color:hsl(var(--amber))]">
                      DRAFT · v{p.version.split(" ")[0]}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">{p.summary}</p>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function PolicyDetail({ slug }) {
  const policy = POLICIES[slug];
  if (!policy) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
          <p className="text-muted-foreground">Policy not found.</p>
          <Link to="/legal" className="mt-3 inline-block text-sm text-[color:hsl(var(--teal))] underline underline-offset-2">
            ← All policies
          </Link>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <Link
          to="/legal"
          data-testid="legal-back-link"
          className="text-xs font-mono-label text-[color:hsl(var(--teal))] hover:underline"
        >
          ← All policies
        </Link>
        <h1
          className="mt-3 font-display font-extrabold text-3xl sm:text-4xl"
          data-testid={`legal-title-${slug}`}
        >
          {policy.title}
        </h1>
        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <span className="font-mono-label uppercase tracking-wider text-[color:hsl(var(--amber))]">
            DRAFT · v{policy.version.split(" ")[0]}
          </span>
          <span>Last updated {policy.updated}</span>
        </div>
        <DraftBanner />
        <p className="text-sm text-muted-foreground">{policy.summary}</p>
        <div className="mt-6 space-y-6" data-testid="legal-sections">
          {policy.sections.map((s, i) => (
            <section key={i}>
              <h2 className="font-display font-bold text-base sm:text-lg">{s.h}</h2>
              <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{s.p}</p>
            </section>
          ))}
        </div>
        <div className="mt-10 rounded-2xl border bg-muted/30 p-5 text-xs text-muted-foreground">
          Questions about these drafts? Email <b>legal@cling.app</b> (placeholder). Final
          language will be provided by qualified counsel per jurisdiction before public beta.
        </div>
      </div>
    </div>
  );
}

export default function Legal() {
  const { slug } = useParams();
  return slug ? <PolicyDetail slug={slug} /> : <PolicyIndex />;
}
